/**
 * Hand-drawn nav glyphs rather than an icon dependency: six icons do not
 * justify a package, and drawing them here keeps the stroke weight matched to
 * the rest of the panel. All inherit `currentColor`, so the rail controls
 * active/inactive colour in one place.
 */
interface IconProps {
  size?: number;
}

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

/** Inhalt — a fridge, door divider and two handles. */
export function IconFridge({ size = 26 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="5" y="2.5" width="14" height="19" rx="3" />
      <path d="M5 10h14" />
      <path d="M8.4 6v2" />
      <path d="M8.4 13v2.6" />
    </svg>
  );
}

/** Scannen — the viewfinder brackets from the scan screen, plus a beam. */
export function IconScan({ size = 26 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M4 8.5V6.5A2.5 2.5 0 0 1 6.5 4h2" />
      <path d="M15.5 4h2A2.5 2.5 0 0 1 20 6.5v2" />
      <path d="M20 15.5v2a2.5 2.5 0 0 1-2.5 2.5h-2" />
      <path d="M8.5 20h-2A2.5 2.5 0 0 1 4 17.5v-2" />
      <path d="M4.5 12h15" />
    </svg>
  );
}

/** Kalender — month grid with two hangers. */
export function IconCalendar({ size = 26 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="3.5" y="5" width="17" height="16" rx="2.5" />
      <path d="M3.5 10h17" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
    </svg>
  );
}

/** Einkauf — a shopping bag. */
export function IconBag({ size = 26 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M5.2 7.5h13.6l1 12.2a1.5 1.5 0 0 1-1.5 1.6H5.7a1.5 1.5 0 0 1-1.5-1.6l1-12.2Z" />
      <path d="M9 10.5V6.8a3 3 0 0 1 6 0v3.7" />
    </svg>
  );
}

/** System — sliders. Reads as "settings" more plainly than a gear at 26px. */
export function IconSliders({ size = 26 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M4 6.5h9" />
      <circle cx="16.5" cy="6.5" r="2.3" />
      <path d="M20 12h-9" />
      <circle cx="7.5" cy="12" r="2.3" />
      <path d="M4 17.5h9" />
      <circle cx="16.5" cy="17.5" r="2.3" />
    </svg>
  );
}

/** Notizen — a pen nib over a scribble. */
export function IconPen({ size = 26 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M14.8 4.6 19.4 9.2 8.6 20H4v-4.6L14.8 4.6Z" />
      <path d="M13 6.4 17.6 11" />
    </svg>
  );
}

/** Übersicht — a board of tiles, one large and three small. */
export function IconGrid({ size = 26 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="3" y="3.5" width="8.5" height="17" rx="2.5" />
      <rect x="14.5" y="3.5" width="6.5" height="7" rx="2.5" />
      <rect x="14.5" y="13.5" width="6.5" height="7" rx="2.5" />
    </svg>
  );
}

/** Kacheln — an even 2×2 grid, distinct from the dashboard's uneven board. */
export function IconTiles({ size = 26 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="2.2" />
      <rect x="13" y="3.5" width="7.5" height="7.5" rx="2.2" />
      <rect x="3.5" y="13" width="7.5" height="7.5" rx="2.2" />
      <rect x="13" y="13" width="7.5" height="7.5" rx="2.2" />
    </svg>
  );
}

/** Liste — rows with their leading markers. */
export function IconList({ size = 26 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M4 6.5h.01M4 12h.01M4 17.5h.01" />
      <path d="M8.5 6.5H20M8.5 12H20M8.5 17.5H20" />
    </svg>
  );
}

/** Hinzufügen. */
export function IconPlus({ size = 26 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** Wegwerfen — a bin with its lid and handle. */
export function IconTrash({ size = 26 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M4 6.5h16" />
      <path d="M9.5 6.5V4.8a1.3 1.3 0 0 1 1.3-1.3h2.4a1.3 1.3 0 0 1 1.3 1.3v1.7" />
      <path d="M6.5 6.5 7.4 19a1.6 1.6 0 0 0 1.6 1.5h6a1.6 1.6 0 0 0 1.6-1.5l.9-12.5" />
      <path d="M10.4 10.2v6.4M13.6 10.2v6.4" />
    </svg>
  );
}
